/**
 * ═══════════════════════════════════════════════════════════════════════════
 * QR KODLAYICI — asılılıqsız, byte rejimi, versiya 1-10, ECC L/M
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * NİYƏ ÖZÜMÜZ YAZIRIQ. TASKS.md-də qeyd olunub: `pnpm add qrcode` bu mühitdə
 * corepack imza xətası ilə alınmır və QR şəkli ona görə yoxdur. Kodlayıcı saf
 * arifmetikadır (DOM yoxdur, ~250 sətir) — paket asılılığını həmişəlik aradan
 * qaldırır və bundle-a cəmi ~3 KB əlavə edir. TV üçün bu, `qrcode` paketinin
 * (~40 KB) yerinə daha yaxşıdır.
 *
 * Alqoritm ISO/IEC 18004-dür; quruluş Project Nayuki-nin ictimai izahındakı
 * kimidir (Reed-Solomon qalığı, 8 maska + cərimə balı ilə seçim).
 *
 * ⚠ Doğruluq `scripts/verify-qr.mjs` ilə yoxlanılır: matris PNG-ə çevrilir və
 *   macOS CoreImage QR detektoru ilə GERİ OXUNUR (round-trip).
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type EcLevel = 'L' | 'M'

/** Versiya 1-10 üçün blok başına EC söz sayı. */
const ECC_PER_BLOCK: Record<EcLevel, number[]> = {
  L: [7, 10, 15, 20, 26, 18, 20, 24, 30, 18],
  M: [10, 16, 26, 18, 24, 16, 18, 22, 22, 26],
}

/** Versiya 1-10 üçün EC blok sayı. */
const NUM_BLOCKS: Record<EcLevel, number[]> = {
  L: [1, 1, 1, 1, 1, 2, 2, 2, 2, 4],
  M: [1, 1, 1, 2, 2, 4, 4, 4, 5, 5],
}

/** Format sahəsindəki 2 bitlik səviyyə kodu. */
const FORMAT_BITS: Record<EcLevel, number> = { L: 1, M: 0 }

const MAX_VERSION = 10

/**
 * QR kodun xanalar matrisi — kətan üzərində çəkmək üçün.
 * Xarici kitabxana YOXDUR: TV bundle-ını yüngül saxlamaq üçün minimal kodlayıcı yazılıb.
 */
export interface QrMatrix {
  size: number
  /** `true` = tünd modul. Sətir-sütun: `modules[y][x]`. */
  modules: boolean[][]
}

/** Mətni QR matrisinə çevirir. Sığmazsa `null` qaytarır (versiya həddi 10). */
export function encodeQr(text: string, ecLevel: EcLevel): QrMatrix | null {
  const bytes = utf8Bytes(text)

  let version = 0
  for (let v = 1; v <= MAX_VERSION; v++) {
    const capacityBits = numDataCodewords(v, ecLevel) * 8
    const needed = 4 + charCountBits(v) + bytes.length * 8
    if (needed <= capacityBits) {
      version = v
      break
    }
  }
  if (version === 0) return null

  const dataCodewords = buildDataCodewords(bytes, version, ecLevel)
  const codewords = addEccAndInterleave(dataCodewords, version, ecLevel)
  return buildMatrix(codewords, version, ecLevel)
}

// ── Məlumat sözləri ────────────────────────────────────────────────────────

function buildDataCodewords(bytes: number[], version: number, ecLevel: EcLevel): number[] {
  const bits: number[] = []
  appendBits(bits, 0b0100, 4) // byte rejimi
  appendBits(bits, bytes.length, charCountBits(version))
  for (let i = 0; i < bytes.length; i++) appendBits(bits, bytes[i] as number, 8)

  const capacityBits = numDataCodewords(version, ecLevel) * 8

  // Sonlandırıcı — ən çox 4 sıfır bit.
  appendBits(bits, 0, Math.min(4, capacityBits - bits.length))
  // Bayt sərhədinə qədər sıfır.
  appendBits(bits, 0, (8 - (bits.length % 8)) % 8)

  // Doldurucu baytlar — standartda növbələşən 0xEC / 0x11.
  for (let pad = 0xec; bits.length < capacityBits; pad ^= 0xec ^ 0x11) appendBits(bits, pad, 8)

  const codewords: number[] = []
  for (let i = 0; i < bits.length; i += 8) {
    let value = 0
    for (let j = 0; j < 8; j++) value = (value << 1) | (bits[i + j] as number)
    codewords.push(value)
  }
  return codewords
}

function appendBits(bits: number[], value: number, count: number): void {
  for (let i = count - 1; i >= 0; i--) bits.push((value >>> i) & 1)
}

function charCountBits(version: number): number {
  return version <= 9 ? 8 : 16
}

function numRawDataModules(version: number): number {
  let result = (16 * version + 128) * version + 64
  if (version >= 2) {
    const numAlign = Math.floor(version / 7) + 2
    result -= (25 * numAlign - 10) * numAlign - 55
    if (version >= 7) result -= 36
  }
  return result
}

function numDataCodewords(version: number, ecLevel: EcLevel): number {
  const raw = Math.floor(numRawDataModules(version) / 8)
  return raw - (ECC_PER_BLOCK[ecLevel][version - 1] as number) * (NUM_BLOCKS[ecLevel][version - 1] as number)
}

// ── Reed-Solomon ───────────────────────────────────────────────────────────

/** GF(2^8) vurma, azaldıcı polinom 0x11D — cədvəlsiz, carry-less. */
function gfMul(x: number, y: number): number {
  let z = 0
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d)
    z ^= ((y >>> i) & 1) * x
  }
  return z & 0xff
}

/** `new Array(n).fill(v)` əvəzinə — oxunaqlı və köhnə mühərrikdə də təhlükəsiz. */
function filled<T>(length: number, value: T): T[] {
  const out: T[] = []
  for (let i = 0; i < length; i++) out.push(value)
  return out
}

function rsDivisor(degree: number): number[] {
  const result = filled(degree, 0)
  result[degree - 1] = 1
  let root = 1
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      result[j] = gfMul(result[j] as number, root)
      if (j + 1 < degree) result[j] = (result[j] as number) ^ (result[j + 1] as number)
    }
    root = gfMul(root, 0x02)
  }
  return result
}

function rsRemainder(data: number[], divisor: number[]): number[] {
  const result = filled(divisor.length, 0)
  for (let i = 0; i < data.length; i++) {
    const factor = (data[i] as number) ^ (result.shift() as number)
    result.push(0)
    for (let j = 0; j < divisor.length; j++) {
      result[j] = (result[j] as number) ^ gfMul(divisor[j] as number, factor)
    }
  }
  return result
}

function addEccAndInterleave(data: number[], version: number, ecLevel: EcLevel): number[] {
  const numBlocks = NUM_BLOCKS[ecLevel][version - 1] as number
  const blockEccLen = ECC_PER_BLOCK[ecLevel][version - 1] as number
  const rawCodewords = Math.floor(numRawDataModules(version) / 8)
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks)
  const shortBlockLen = Math.floor(rawCodewords / numBlocks)

  const blocks: number[][] = []
  const divisor = rsDivisor(blockEccLen)
  for (let i = 0, k = 0; i < numBlocks; i++) {
    const length = shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1)
    const chunk = data.slice(k, k + length)
    k += chunk.length
    const ecc = rsRemainder(chunk, divisor)
    // Qısa bloklara bir yer saxlanılır ki, çarpazlama sadə sütun gedişi olsun.
    if (i < numShortBlocks) chunk.push(0)
    blocks.push(chunk.concat(ecc))
  }

  const result: number[] = []
  const rowLength = (blocks[0] as number[]).length
  for (let i = 0; i < rowLength; i++) {
    for (let j = 0; j < blocks.length; j++) {
      // Qısa bloklardakı saxlanmış yer buraxılır.
      if (i !== shortBlockLen - blockEccLen || j >= numShortBlocks) {
        result.push((blocks[j] as number[])[i] as number)
      }
    }
  }
  return result
}

// ── Matris ─────────────────────────────────────────────────────────────────

function buildMatrix(codewords: number[], version: number, ecLevel: EcLevel): QrMatrix {
  const size = version * 4 + 17
  const modules: boolean[][] = []
  const isFunction: boolean[][] = []
  for (let y = 0; y < size; y++) {
    modules.push(filled(size, false))
    isFunction.push(filled(size, false))
  }

  const setFn = (x: number, y: number, dark: boolean) => {
    ;(modules[y] as boolean[])[x] = dark
    ;(isFunction[y] as boolean[])[x] = true
  }

  // Zaman zolaqları.
  for (let i = 0; i < size; i++) {
    setFn(6, i, i % 2 === 0)
    setFn(i, 6, i % 2 === 0)
  }

  // Axtarış naxışları (separatorlarla birlikdə 9×9 sahə).
  const drawFinder = (cx: number, cy: number) => {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy))
        const x = cx + dx
        const y = cy + dy
        if (x >= 0 && x < size && y >= 0 && y < size) setFn(x, y, dist !== 2 && dist !== 4)
      }
    }
  }
  drawFinder(3, 3)
  drawFinder(size - 4, 3)
  drawFinder(3, size - 4)

  // Hizalama naxışları.
  const positions = alignmentPositions(version, size)
  for (let i = 0; i < positions.length; i++) {
    for (let j = 0; j < positions.length; j++) {
      // Üç künc axtarış naxışı ilə örtüşür — atlanır.
      if ((i === 0 && j === 0) || (i === 0 && j === positions.length - 1) || (i === positions.length - 1 && j === 0)) {
        continue
      }
      const cx = positions[j] as number
      const cy = positions[i] as number
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          setFn(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1)
        }
      }
    }
  }

  // Format və versiya sahələri əvvəlcə rezerv edilir (maska seçimindən sonra
  // yenidən yazılır) — data yerləşdirməsi onların üstündən keçməsin.
  drawFormatBits(setFn, size, ecLevel, 0)
  drawVersionBits(setFn, size, version)

  // Data bitləri — sağ aşağıdan başlayan ziqzaq.
  let bitIndex = 0
  const totalBits = codewords.length * 8
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j
        const upward = ((right + 1) & 2) === 0
        const y = upward ? size - 1 - vert : vert
        if (!(isFunction[y] as boolean[])[x] && bitIndex < totalBits) {
          const byte = codewords[bitIndex >>> 3] as number
          ;(modules[y] as boolean[])[x] = ((byte >>> (7 - (bitIndex & 7))) & 1) !== 0
          bitIndex++
        }
        // Qalan modullar açıq (false) qalır — «remainder bit»-lər budur.
      }
    }
  }

  // Maska seçimi — 8 variantdan ən aşağı cərimə balı olan.
  let bestMask = 0
  let bestPenalty = Infinity
  for (let mask = 0; mask < 8; mask++) {
    applyMask(modules, isFunction, size, mask)
    drawFormatBits(setFn, size, ecLevel, mask)
    const penalty = penaltyScore(modules, size)
    if (penalty < bestPenalty) {
      bestPenalty = penalty
      bestMask = mask
    }
    applyMask(modules, isFunction, size, mask) // XOR — geri qaytarır
  }
  applyMask(modules, isFunction, size, bestMask)
  drawFormatBits(setFn, size, ecLevel, bestMask)

  return { size, modules }
}

function alignmentPositions(version: number, size: number): number[] {
  if (version === 1) return []
  const count = Math.floor(version / 7) + 2
  const step = Math.ceil((version * 4 + 4) / (count * 2 - 2)) * 2
  const result = [6]
  for (let pos = size - 7; result.length < count; pos -= step) result.splice(1, 0, pos)
  return result
}

type SetFn = (x: number, y: number, dark: boolean) => void

function drawFormatBits(setFn: SetFn, size: number, ecLevel: EcLevel, mask: number): void {
  const data = (FORMAT_BITS[ecLevel] << 3) | mask
  let rem = data
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537)
  const bits = ((data << 10) | rem) ^ 0x5412
  const bit = (i: number) => ((bits >>> i) & 1) !== 0

  for (let i = 0; i <= 5; i++) setFn(8, i, bit(i))
  setFn(8, 7, bit(6))
  setFn(8, 8, bit(7))
  setFn(7, 8, bit(8))
  for (let i = 9; i < 15; i++) setFn(14 - i, 8, bit(i))

  for (let i = 0; i < 8; i++) setFn(size - 1 - i, 8, bit(i))
  for (let i = 8; i < 15; i++) setFn(8, size - 15 + i, bit(i))
  setFn(8, size - 8, true) // həmişə tünd modul
}

function drawVersionBits(setFn: SetFn, size: number, version: number): void {
  if (version < 7) return
  let rem = version
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25)
  const bits = (version << 12) | rem
  for (let i = 0; i < 18; i++) {
    const dark = ((bits >>> i) & 1) !== 0
    const a = size - 11 + (i % 3)
    const b = Math.floor(i / 3)
    setFn(a, b, dark)
    setFn(b, a, dark)
  }
}

function applyMask(modules: boolean[][], isFunction: boolean[][], size: number, mask: number): void {
  for (let y = 0; y < size; y++) {
    const row = modules[y] as boolean[]
    const fnRow = isFunction[y] as boolean[]
    for (let x = 0; x < size; x++) {
      if (fnRow[x]) continue
      let invert = false
      switch (mask) {
        case 0:
          invert = (x + y) % 2 === 0
          break
        case 1:
          invert = y % 2 === 0
          break
        case 2:
          invert = x % 3 === 0
          break
        case 3:
          invert = (x + y) % 3 === 0
          break
        case 4:
          invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0
          break
        case 5:
          invert = ((x * y) % 2) + ((x * y) % 3) === 0
          break
        case 6:
          invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0
          break
        default:
          invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0
          break
      }
      if (invert) row[x] = !row[x]
    }
  }
}

const N1 = 3
const N2 = 3
const N3 = 40
const N4 = 10

function penaltyScore(modules: boolean[][], size: number): number {
  let result = 0

  // Sətirlər: ardıcıl eyni rəng + axtarışabənzər naxış.
  for (let y = 0; y < size; y++) {
    const row = modules[y] as boolean[]
    let runColor = false
    let runLength = 0
    const history = [0, 0, 0, 0, 0, 0, 0]
    for (let x = 0; x < size; x++) {
      if (row[x] === runColor) {
        runLength++
        if (runLength === 5) result += N1
        else if (runLength > 5) result++
      } else {
        addHistory(history, runLength, size)
        if (!runColor) result += countFinderLike(history) * N3
        runColor = row[x] as boolean
        runLength = 1
      }
    }
    result += terminateAndCount(history, runColor, runLength, size) * N3
  }

  // Sütunlar: eyni hesablama, transpozisiya ilə.
  for (let x = 0; x < size; x++) {
    let runColor = false
    let runLength = 0
    const history = [0, 0, 0, 0, 0, 0, 0]
    for (let y = 0; y < size; y++) {
      const cell = (modules[y] as boolean[])[x] as boolean
      if (cell === runColor) {
        runLength++
        if (runLength === 5) result += N1
        else if (runLength > 5) result++
      } else {
        addHistory(history, runLength, size)
        if (!runColor) result += countFinderLike(history) * N3
        runColor = cell
        runLength = 1
      }
    }
    result += terminateAndCount(history, runColor, runLength, size) * N3
  }

  // 2×2 eyni rəngli bloklar.
  for (let y = 0; y < size - 1; y++) {
    const row = modules[y] as boolean[]
    const next = modules[y + 1] as boolean[]
    for (let x = 0; x < size - 1; x++) {
      const c = row[x]
      if (c === row[x + 1] && c === next[x] && c === next[x + 1]) result += N2
    }
  }

  // Tünd/açıq balansı.
  let dark = 0
  for (let y = 0; y < size; y++) {
    const row = modules[y] as boolean[]
    for (let x = 0; x < size; x++) if (row[x]) dark++
  }
  const total = size * size
  const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1
  result += k * N4

  return result
}

function addHistory(history: number[], runLength: number, size: number): void {
  let length = runLength
  if (history[0] === 0) length += size // sol/üst açıq kənar
  history.pop()
  history.unshift(length)
}

function terminateAndCount(history: number[], runColor: boolean, runLength: number, size: number): number {
  let length = runLength
  if (runColor) {
    addHistory(history, length, size)
    length = 0
  }
  length += size // sağ/alt açıq kənar
  addHistory(history, length, size)
  return countFinderLike(history)
}

function countFinderLike(history: number[]): number {
  const n = history[1] as number
  const core =
    n > 0 && history[2] === n && history[3] === n * 3 && history[4] === n && history[5] === n
  return (
    (core && (history[0] as number) >= n * 4 && (history[6] as number) >= n ? 1 : 0) +
    (core && (history[6] as number) >= n * 4 && (history[0] as number) >= n ? 1 : 0)
  )
}

// ── UTF-8 ──────────────────────────────────────────────────────────────────

/** `TextEncoder` webOS 3-də yoxdur — əl ilə kodlaşdırırıq. */
function utf8Bytes(text: string): number[] {
  const out: number[] = []
  for (let i = 0; i < text.length; i++) {
    let code = text.charCodeAt(i)
    // Surroqat cütü.
    if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      const low = text.charCodeAt(i + 1)
      if (low >= 0xdc00 && low <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (low - 0xdc00)
        i++
      }
    }
    if (code < 0x80) {
      out.push(code)
    } else if (code < 0x800) {
      out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f))
    } else if (code < 0x10000) {
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f))
    } else {
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      )
    }
  }
  return out
}

// ── Çəkiliş ────────────────────────────────────────────────────────────────

/**
 * Matrisi `<canvas>`-a çəkir.
 *
 * ⚠ SVG DEYİL, canvas: köhnə TV-də 1000+ `<rect>` elementli SVG hər kadrda
 * yenidən rasterləşdirilir; canvas bir dəfə çəkilir və şəkil kimi qalır.
 * Kvadratlar sətir-sətir birləşdirilir → `fillRect` çağırışı ~10 dəfə azalır.
 */
export function drawQr(matrix: QrMatrix, moduleSize: number, quietZone: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  const side = (matrix.size + quietZone * 2) * moduleSize
  canvas.width = side
  canvas.height = side
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas

  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, side, side)
  ctx.fillStyle = '#000000'

  for (let y = 0; y < matrix.size; y++) {
    const row = matrix.modules[y] as boolean[]
    let runStart = -1
    for (let x = 0; x <= matrix.size; x++) {
      const dark = x < matrix.size ? row[x] === true : false
      if (dark && runStart < 0) runStart = x
      if (!dark && runStart >= 0) {
        ctx.fillRect(
          (runStart + quietZone) * moduleSize,
          (y + quietZone) * moduleSize,
          (x - runStart) * moduleSize,
          moduleSize,
        )
        runStart = -1
      }
    }
  }
  return canvas
}
